import type { JSX } from 'react';
import type { MouseEvent } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { TagMark } from '@/brand/TagMark';
import { LangToggle } from './LangToggle';

/**
 * NAV-01 — sticky top navigation (replaces Phase 2 minimal).
 *
 * Layout (per design-reference/predikt-v2/project/site-sections.jsx Nav() + predikt-site.css L45-67):
 *  - Sticky top, z-50, paper background, 1px rule bottom border, height 64px.
 *  - Container max-w-[1240px] mx-auto px-6 — matches every other section's pattern.
 *  - Left:   <TagMark size={28}/> wrapped in <Link to="/">.
 *  - Center: 5 anchor links (font-mono 12px, tracking-[0.1em] uppercase, text-dim hover:text-ink).
 *  - Right:  <LangToggle/> + single "Join beta" CTA (h-10) anchored to #waitlist.
 *  - Anchor click smooth-scrolls to section, falling back to instant on prefers-reduced-motion.
 *
 * Section IDs (D-09): all Latin ('#markets', '#how', '#waitlist', '#about', '#docs').
 * '#about' and '#docs' have no target sections in Phase 3 — they no-op gracefully
 * (getElementById returns null, and the handler returns early).
 *
 * Pre-launch CTA strategy (2026-04-27): "Sign in" + "Open app" both removed —
 * the app does not exist publicly yet, so the only meaningful primary action
 * is "join the beta waitlist". Sign-in returns alongside CTA-01 in Phase 5.
 */

const ANCHOR_IDS = ['markets', 'how', 'waitlist', 'about', 'docs'] as const;
type AnchorId = (typeof ANCHOR_IDS)[number];

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

function handleAnchorClick(targetId: AnchorId, e: MouseEvent<HTMLAnchorElement>): void {
  e.preventDefault();
  const el = document.getElementById(targetId);
  if (!el) return; // no target section yet → graceful no-op (D-17)
  el.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'start' });
}

export function Nav(): JSX.Element {
  const { t } = useTranslation();

  return (
    <header className="bg-paper border-rule sticky top-0 z-50 border-b">
      {/* Container: max-w-[1240px] mx-auto px-6 — same pattern as every other section.
          The previous w-[min(1240px,100%-48px)] was invalid CSS — min() needs
          calc(100% - 48px), not bare arithmetic — which made the nav fall back
          to filling its parent on wide viewports. */}
      <nav className="mx-auto flex h-16 max-w-[1240px] items-center justify-between px-6">
        {/* Left: TagMark logo linking to home */}
        <Link to="/" aria-label="Predikt — home" className="flex items-center gap-3.5">
          <TagMark size={28} />
        </Link>

        {/* Center: 5 anchor links (hidden below md — matches design .nav-links display:none @640) */}
        <div className="hidden items-center gap-7 font-mono text-[12px] tracking-[0.1em] uppercase md:flex">
          {ANCHOR_IDS.map((id) => (
            <a
              key={id}
              href={`#${id}`}
              onClick={(e) => handleAnchorClick(id, e)}
              className="text-dim hover:text-ink transition-colors"
            >
              {t(`nav.links.${id}` as const)}
            </a>
          ))}
        </div>

        {/* Right: LangToggle + single "Join beta" primary CTA */}
        <div className="flex items-center gap-2.5">
          <LangToggle />
          {/* btn primary: 40px height, ink bg, paper fg, hover: orange bg; anchored to #waitlist */}
          <a
            href="#waitlist"
            onClick={(e) => handleAnchorClick('waitlist', e)}
            className="border-ink bg-ink text-paper hover:bg-orange hover:border-orange hover:text-ink rounded-pill inline-flex h-10 items-center justify-center gap-2.5 border px-[18px] font-mono text-[11px] font-semibold tracking-[0.2em] whitespace-nowrap uppercase transition-colors"
          >
            {t('nav.cta.joinBeta')}
            <span aria-hidden="true" className="-translate-y-px text-[14px] leading-none">
              →
            </span>
          </a>
        </div>
      </nav>
    </header>
  );
}

export default Nav;
